/**
 * Public SEO landing pages — bilingual but strictly separated by URL.
 *  - EN: /en/ai-lead-generation-software/
 *  - FR: /fr/logiciel-generation-leads-ia/
 *
 * Renders a single shared component but locks the entire page (content,
 * <html lang>, canonical, hreflang) to the locale prop. Each URL ever
 * serves only one language.
 *
 * Reuses SpaceLeadX design tokens (slate-950 background, electric blue
 * accent, Manrope headings) and the existing shadcn primitives (Button,
 * Card, Badge, Accordion). Header / Footer are local and visually
 * identical to the main LandingPage but live in this file to avoid
 * touching the existing landing.
 */

import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '@/components/ui/accordion';
import {
  Sparkles, ArrowRight, ChevronRight, ShieldCheck, Zap, Check,
  Users, Wrench, MapPin, Ruler, Wallet, CalendarClock,
  Camera, ClipboardList, MousePointerClick, Filter, MessageSquare,
  BarChart3, Database, TrendingUp, Building2, Hammer, Home,
  HardHat, Plug, Lightbulb, Paintbrush, Megaphone,
} from 'lucide-react';

// ---------------------------------------------------------------------------
// Canonical site host (used for absolute URLs in <head> / JSON-LD)
// ---------------------------------------------------------------------------
const SITE_HOST = 'https://spaceleadx.ai';
const PATHS = {
  en: '/en/ai-lead-generation-software/',
  fr: '/fr/logiciel-generation-leads-ia/',
};
const OG_IMAGE = `${SITE_HOST}/og-image.png`;

// ---------------------------------------------------------------------------
// Content (intentionally inline — no lorem, no external i18n keys)
// ---------------------------------------------------------------------------
const CONTENT = {
  en: {
    htmlLang: 'en',
    title: 'AI Lead Generation Software for Service Businesses | SpaceLeadX',
    metaDescription:
      'Generate, qualify and manage more leads with SpaceLeadX AI lead generation software built for contractors, agencies and service businesses.',
    ogTitle: 'AI Lead Generation Software for Service Businesses | SpaceLeadX',
    ogDescription:
      'Generate, qualify and manage more leads with SpaceLeadX AI lead generation software built for contractors, agencies and service businesses.',
    breadcrumbHome: 'Home',
    breadcrumbCurrent: 'AI Lead Generation Software',
    headerSignIn: 'Sign in',
    headerCta: 'Get started',
    nav: { features: 'Features', pricing: 'Pricing' },
    hero: {
      eyebrow: 'AI Lead Generation Software',
      h1: 'AI Lead Generation Software Built to Turn Website Visitors Into Qualified Leads',
      sub: 'SpaceLeadX helps contractors, agencies and service businesses capture more opportunities, qualify prospects automatically and convert website visitors into real customers.',
      ctaPrimary: 'Request a Demo',
      ctaSecondary: 'See How It Works',
      trust: [
        { icon: ShieldCheck, label: 'Built for service businesses' },
        { icon: Zap, label: '5-minute setup' },
        { icon: ShieldCheck, label: 'AI + human support included' },
      ],
    },
    section1: {
      h2: 'Turn Your Website Into an AI Lead Generation System',
      paragraphs: [
        'Traditional contact forms usually collect only a name, phone number and email address.',
        'SpaceLeadX goes further by guiding visitors through an intelligent and interactive process based on their service needs, project type, location, timeline and level of urgency.',
        'Every completed submission becomes a structured lead your team can review, contact and manage.',
      ],
    },
    section2: {
      h2: 'Capture Better Information From Every Prospect',
      items: [
        { icon: Users, label: 'Contact information' },
        { icon: Wrench, label: 'Service required' },
        { icon: MapPin, label: 'Project location' },
        { icon: Ruler, label: 'Estimated project size' },
        { icon: Wallet, label: 'Budget range' },
        { icon: CalendarClock, label: 'Timeline and urgency' },
        { icon: Camera, label: 'Photos and supporting information' },
        { icon: ClipboardList, label: 'Custom project details' },
      ],
    },
    section3: {
      h2: 'Why Businesses Choose SpaceLeadX',
      cards: [
        {
          icon: MousePointerClick,
          title: 'Capture More Website Visitors',
          desc: 'Interactive forms guide visitors one question at a time, making the experience easier and reducing friction.',
        },
        {
          icon: Filter,
          title: 'Automatically Qualify Every Lead',
          desc: 'Collect the information your team needs before the first phone call.',
        },
        {
          icon: MessageSquare,
          title: 'Respond Faster',
          desc: 'Receive structured lead information quickly while the prospect is still interested.',
        },
        {
          icon: BarChart3,
          title: 'Understand Form Abandonment',
          desc: 'Track form starts, completed forms, completion rates and abandonment by step.',
        },
        {
          icon: Database,
          title: 'Manage Leads in One Place',
          desc: 'Centralize contact details, form responses and project information.',
        },
        {
          icon: TrendingUp,
          title: 'Improve Marketing Performance',
          desc: 'Convert more of the traffic already coming from SEO, advertising and social media.',
        },
      ],
    },
    section4: {
      h2: 'AI Lead Generation for Contractors and Service Businesses',
      paragraph:
        'SpaceLeadX is designed for companies that receive quote requests, service inquiries or project estimate requests.',
      industries: [
        { icon: Paintbrush, label: 'Landscaping companies' },
        { icon: HardHat, label: 'Paving contractors' },
        { icon: Building2, label: 'Foundation repair companies' },
        { icon: Home, label: 'Roofing contractors' },
        { icon: Hammer, label: 'Excavation companies' },
        { icon: Wrench, label: 'Plumbers' },
        { icon: Plug, label: 'Electricians' },
        { icon: Lightbulb, label: 'Home improvement companies' },
        { icon: Megaphone, label: 'Marketing agencies' },
      ],
    },
    section5: {
      h2: 'Replace Basic Contact Forms With Intelligent Lead Forms',
      paragraphs: [
        'A standard contact form tells you who contacted you.',
        'An intelligent AI form helps you understand what the prospect needs, how serious the project is and what your team should do next.',
        'SpaceLeadX can replace your current contact form or operate alongside it as an AI-powered quote and qualification option.',
      ],
    },
    section6: {
      h2: 'Improve Lead Quality Without Increasing Advertising Costs',
      paragraphs: [
        'Many businesses invest in SEO, Google Ads and social media but lose potential customers because their website does not convert effectively.',
        'SpaceLeadX helps improve the value of your existing traffic by turning more visitors into structured and qualified leads.',
      ],
    },
    section7: {
      h2: 'Built for Growth',
      paragraphs: [
        'Start with one intelligent lead form and expand into multiple services, locations, campaigns or customer journeys.',
        'SpaceLeadX can support local businesses, agencies and organizations operating across multiple markets.',
      ],
    },
    finalCta: {
      h2: 'Start Generating Better Leads With AI',
      text: 'Capture more opportunities, qualify prospects automatically and give your team the information needed to close more business.',
      button: 'See SpaceLeadX in Action',
    },
    faq: {
      h2: 'Frequently Asked Questions',
      items: [
        {
          q: 'What is AI lead generation software?',
          a: 'AI lead generation software uses artificial intelligence and automation to capture, organize and qualify potential customers.',
        },
        {
          q: 'Can SpaceLeadX replace my current contact form?',
          a: 'Yes. SpaceLeadX can replace a traditional contact form or operate alongside it as an intelligent quote and qualification option.',
        },
        {
          q: 'Does SpaceLeadX automatically generate leads?',
          a: 'SpaceLeadX helps convert existing website traffic into structured leads and supports the qualification and follow-up process.',
        },
        {
          q: 'Can the questions be customized?',
          a: 'Yes. Questions, services, qualification steps and form logic can be adapted to each industry and business.',
        },
        {
          q: 'Is SpaceLeadX suitable for small businesses?',
          a: 'Yes. SpaceLeadX can be used by small local businesses, growing contractors, agencies and larger service organizations.',
        },
      ],
    },
    footer: {
      tagline: 'Made in Quebec.',
      links: [
        { label: 'Features', to: '/#features' },
        { label: 'Pricing', to: '/#pricing' },
        { label: 'Sign up', to: '/signup' },
        { label: 'Terms of use', to: '/en/terms-of-use' },
        { label: 'Privacy', to: '/en/privacy-policy' },
      ],
    },
    internalLinks: {
      h3: 'Explore SpaceLeadX',
      items: [
        { label: 'Features', to: '/#features' },
        { label: 'Pricing', to: '/#pricing' },
        { label: 'Create your account', to: '/signup' },
      ],
    },
  },
  fr: {
    htmlLang: 'fr',
    title: 'Logiciel de génération de leads par IA | SpaceLeadX',
    metaDescription:
      'Générez et qualifiez plus de prospects avec le logiciel de génération de leads par IA SpaceLeadX, conçu pour les entrepreneurs et entreprises de services.',
    ogTitle: 'Logiciel de génération de leads par IA | SpaceLeadX',
    ogDescription:
      'Générez et qualifiez plus de prospects avec le logiciel de génération de leads par IA SpaceLeadX, conçu pour les entrepreneurs et entreprises de services.',
    breadcrumbHome: 'Accueil',
    breadcrumbCurrent: 'Logiciel de génération de leads par IA',
    headerSignIn: 'Se connecter',
    headerCta: 'Activer mon espace',
    nav: { features: 'Fonctionnalités', pricing: 'Tarifs' },
    hero: {
      eyebrow: 'Logiciel de génération de leads par IA',
      h1: 'Logiciel de génération de leads par IA conçu pour convertir vos visiteurs en clients potentiels',
      sub: 'SpaceLeadX aide les entrepreneurs, les agences et les entreprises de services à obtenir plus de demandes, qualifier automatiquement leurs prospects et convertir davantage de visiteurs en clients.',
      ctaPrimary: 'Demander une démonstration',
      ctaSecondary: 'Voir comment ça fonctionne',
      trust: [
        { icon: ShieldCheck, label: 'Conçu pour les entreprises de services' },
        { icon: Zap, label: 'Configuration en 5 minutes' },
        { icon: ShieldCheck, label: 'IA + Support humain inclus' },
      ],
    },
    section1: {
      h2: 'Transformez votre site Web en système de génération de prospects',
      paragraphs: [
        'Les formulaires de contact traditionnels recueillent généralement seulement un nom, un numéro de téléphone et une adresse courriel.',
        'SpaceLeadX va plus loin en guidant chaque visiteur dans un processus intelligent et interactif selon le service demandé, le type de projet, la localisation, l’échéancier et le niveau d’urgence.',
        'Chaque formulaire complété devient un prospect structuré que votre équipe peut consulter, contacter et gérer.',
      ],
    },
    section2: {
      h2: 'Recueillez de meilleures informations sur chaque prospect',
      items: [
        { icon: Users, label: 'Coordonnées du client' },
        { icon: Wrench, label: 'Service recherché' },
        { icon: MapPin, label: 'Ville du projet' },
        { icon: Ruler, label: 'Dimension approximative' },
        { icon: Wallet, label: 'Budget disponible' },
        { icon: CalendarClock, label: 'Échéancier et urgence' },
        { icon: Camera, label: 'Photos du projet' },
        { icon: ClipboardList, label: 'Informations personnalisées' },
      ],
    },
    section3: {
      h2: 'Pourquoi choisir SpaceLeadX ?',
      cards: [
        {
          icon: MousePointerClick,
          title: 'Convertir davantage de visiteurs',
          desc: 'Les formulaires interactifs guident le visiteur une question à la fois et réduisent les obstacles.',
        },
        {
          icon: Filter,
          title: 'Qualifier automatiquement les prospects',
          desc: 'Recueillez les informations nécessaires avant même le premier appel.',
        },
        {
          icon: MessageSquare,
          title: 'Répondre plus rapidement',
          desc: 'Recevez rapidement les détails du projet pendant que l’intérêt du prospect est élevé.',
        },
        {
          icon: BarChart3,
          title: 'Comprendre les abandons',
          desc: 'Suivez les formulaires commencés, complétés, le taux de complétion et les abandons par étape.',
        },
        {
          icon: Database,
          title: 'Centraliser les demandes',
          desc: 'Regroupez les coordonnées, les réponses et les détails du projet au même endroit.',
        },
        {
          icon: TrendingUp,
          title: 'Améliorer les performances marketing',
          desc: 'Convertissez davantage du trafic provenant du SEO, de la publicité et des réseaux sociaux.',
        },
      ],
    },
    section4: {
      h2: 'Une solution conçue pour les entrepreneurs et entreprises de services',
      paragraph:
        'SpaceLeadX est conçu pour les entreprises qui reçoivent des demandes de soumission, des demandes de service ou des demandes d’estimation de projet.',
      industries: [
        { icon: Paintbrush, label: 'Entreprises d’aménagement paysager' },
        { icon: HardHat, label: 'Entrepreneurs en pavé uni' },
        { icon: Building2, label: 'Entreprises de fondation' },
        { icon: Home, label: 'Entreprises de toiture' },
        { icon: Hammer, label: 'Entrepreneurs en excavation' },
        { icon: Wrench, label: 'Plombiers' },
        { icon: Plug, label: 'Électriciens' },
        { icon: Lightbulb, label: 'Entreprises de rénovation' },
        { icon: Megaphone, label: 'Agences marketing' },
      ],
    },
    section5: {
      h2: 'Remplacez les formulaires traditionnels par des formulaires intelligents',
      paragraphs: [
        'Un formulaire de contact standard vous indique qui vous a écrit.',
        'Un formulaire intelligent vous aide à comprendre ce que le client recherche, la nature de son projet et les prochaines étapes à effectuer.',
        'SpaceLeadX peut remplacer votre formulaire actuel ou fonctionner en parallèle comme option de soumission et de qualification assistée par l’IA.',
      ],
    },
    section6: {
      h2: 'Obtenez plus de valeur de votre trafic actuel',
      paragraphs: [
        'Plusieurs entreprises investissent dans le référencement naturel, Google Ads et les réseaux sociaux, mais perdent des clients potentiels parce que leur site Web ne convertit pas suffisamment.',
        'SpaceLeadX aide à transformer davantage de visiteurs en prospects structurés et qualifiés.',
      ],
    },
    section7: {
      h2: 'Une plateforme conçue pour accompagner votre croissance',
      paragraphs: [
        'Commencez avec un seul formulaire intelligent, puis ajoutez de nouveaux services, secteurs, campagnes ou parcours clients.',
        'SpaceLeadX peut être utilisé par une entreprise locale comme par une organisation desservant plusieurs marchés.',
      ],
    },
    finalCta: {
      h2: 'Commencez à générer de meilleurs prospects grâce à l’IA',
      text: 'Obtenez plus de demandes, qualifiez automatiquement vos prospects et fournissez à votre équipe les informations nécessaires pour conclure davantage de ventes.',
      button: 'Voir SpaceLeadX en action',
    },
    faq: {
      h2: 'Questions fréquentes',
      items: [
        {
          q: 'Qu’est-ce qu’un logiciel de génération de leads par IA ?',
          a: 'Un logiciel de génération de leads par IA utilise l’intelligence artificielle et l’automatisation pour recueillir, organiser et qualifier des clients potentiels.',
        },
        {
          q: 'SpaceLeadX peut-il remplacer mon formulaire actuel ?',
          a: 'Oui. SpaceLeadX peut remplacer un formulaire traditionnel ou fonctionner en parallèle comme option intelligente de soumission et de qualification.',
        },
        {
          q: 'SpaceLeadX génère-t-il automatiquement des prospects ?',
          a: 'SpaceLeadX aide principalement à convertir le trafic existant du site Web en prospects structurés et facilite leur qualification et leur suivi.',
        },
        {
          q: 'Est-il possible de personnaliser les questions ?',
          a: 'Oui. Les questions, les services, les étapes et les règles de qualification peuvent être adaptés à chaque entreprise.',
        },
        {
          q: 'SpaceLeadX convient-il aux petites entreprises ?',
          a: 'Oui. SpaceLeadX peut être utilisé par les petites entreprises locales, les entrepreneurs en croissance, les agences et les organisations possédant plusieurs équipes.',
        },
      ],
    },
    footer: {
      tagline: 'Fait au Québec.',
      links: [
        { label: 'Fonctionnalités', to: '/#features' },
        { label: 'Tarifs', to: '/#pricing' },
        { label: 'Inscription', to: '/signup' },
        { label: "Conditions d'utilisation", to: '/fr/conditions-dutilisation' },
        { label: 'Confidentialité', to: '/fr/politique-de-confidentialite' },
      ],
    },
    internalLinks: {
      h3: 'Explorer SpaceLeadX',
      items: [
        { label: 'Fonctionnalités', to: '/#features' },
        { label: 'Tarifs', to: '/#pricing' },
        { label: 'Créer mon compte', to: '/signup' },
      ],
    },
  },
};

// ---------------------------------------------------------------------------
// <head> injection helper (no react-helmet in the project)
// ---------------------------------------------------------------------------
function useSeoHead(locale) {
  useEffect(() => {
    const c = CONTENT[locale];
    const url = SITE_HOST + PATHS[locale];

    // <html lang> — enforced via a MutationObserver because the global
    // LanguageProvider also writes this attribute on language toggles.
    const prevHtmlLang = document.documentElement.lang;
    document.documentElement.lang = c.htmlLang;
    const langObserver = new MutationObserver(() => {
      if (document.documentElement.lang !== c.htmlLang) {
        document.documentElement.lang = c.htmlLang;
      }
    });
    langObserver.observe(document.documentElement, { attributes: true, attributeFilter: ['lang'] });

    // <title>
    const prevTitle = document.title;
    document.title = c.title;

    // Tags we manage are tagged with data-seo-page="ai-lead-gen-<locale>".
    // Every helper UPSERTS so that the static prerendered HTML (which
    // already carries these tags) is not duplicated at hydration time.
    const tag = `ai-lead-gen-${locale}`;
    const added = []; // elements we created (to remove on unmount)

    const upsertMeta = (selector, attrs) => {
      let el = document.head.querySelector(selector);
      if (!el) {
        el = document.createElement('meta');
        document.head.appendChild(el);
        added.push(el);
      }
      Object.entries(attrs).forEach(([k, v]) => el.setAttribute(k, v));
      el.setAttribute('data-seo-page', tag);
      return el;
    };

    const upsertLink = (selector, rel, href, extraAttrs = {}) => {
      let el = document.head.querySelector(selector);
      if (!el) {
        el = document.createElement('link');
        el.setAttribute('rel', rel);
        document.head.appendChild(el);
        added.push(el);
      }
      el.setAttribute('href', href);
      Object.entries(extraAttrs).forEach(([k, v]) => el.setAttribute(k, v));
      el.setAttribute('data-seo-page', tag);
      return el;
    };

    // Meta description
    const descPrev = document.head.querySelector('meta[name="description"]')?.getAttribute('content');
    const descEl = upsertMeta('meta[name="description"]', {
      name: 'description',
      content: c.metaDescription,
    });

    // Canonical
    upsertLink('link[rel="canonical"]', 'canonical', url);
    // hreflang (3 distinct entries — keyed on the hreflang attribute itself)
    upsertLink('link[rel="alternate"][hreflang="en"]', 'alternate', SITE_HOST + PATHS.en, { hreflang: 'en' });
    upsertLink('link[rel="alternate"][hreflang="fr"]', 'alternate', SITE_HOST + PATHS.fr, { hreflang: 'fr' });
    upsertLink('link[rel="alternate"][hreflang="x-default"]', 'alternate', SITE_HOST + PATHS.en, { hreflang: 'x-default' });

    // Open Graph
    upsertMeta('meta[property="og:title"]', { property: 'og:title', content: c.ogTitle });
    upsertMeta('meta[property="og:description"]', { property: 'og:description', content: c.ogDescription });
    upsertMeta('meta[property="og:url"]', { property: 'og:url', content: url });
    upsertMeta('meta[property="og:type"]', { property: 'og:type', content: 'website' });
    upsertMeta('meta[property="og:image"]', { property: 'og:image', content: OG_IMAGE });
    upsertMeta('meta[property="og:locale"]', {
      property: 'og:locale',
      content: locale === 'fr' ? 'fr_CA' : 'en_US',
    });
    upsertMeta('meta[property="og:locale:alternate"]', {
      property: 'og:locale:alternate',
      content: locale === 'fr' ? 'en_US' : 'fr_CA',
    });

    // Twitter
    upsertMeta('meta[name="twitter:card"]', { name: 'twitter:card', content: 'summary_large_image' });
    upsertMeta('meta[name="twitter:title"]', { name: 'twitter:title', content: c.ogTitle });
    upsertMeta('meta[name="twitter:description"]', { name: 'twitter:description', content: c.ogDescription });
    upsertMeta('meta[name="twitter:image"]', { name: 'twitter:image', content: OG_IMAGE });

    // JSON-LD: SoftwareApplication (no fabricated price)
    const ldSoftware = {
      '@context': 'https://schema.org',
      '@type': 'SoftwareApplication',
      name: 'SpaceLeadX',
      applicationCategory: 'BusinessApplication',
      operatingSystem: 'Web',
      description: c.metaDescription,
      url,
    };
    const ldFaq = {
      '@context': 'https://schema.org',
      '@type': 'FAQPage',
      mainEntity: c.faq.items.map((it) => ({
        '@type': 'Question',
        name: it.q,
        acceptedAnswer: { '@type': 'Answer', text: it.a },
      })),
    };
    const ldBreadcrumb = {
      '@context': 'https://schema.org',
      '@type': 'BreadcrumbList',
      itemListElement: [
        {
          '@type': 'ListItem',
          position: 1,
          name: c.breadcrumbHome,
          item: SITE_HOST + '/',
        },
        {
          '@type': 'ListItem',
          position: 2,
          name: c.breadcrumbCurrent,
          item: url,
        },
      ],
    };

    // JSON-LD blocks (keyed by data-seo-ld so static + runtime never duplicate)
    const upsertLd = (id, obj) => {
      let el = document.head.querySelector(`script[type="application/ld+json"][data-seo-ld="${id}"]`);
      if (!el) {
        el = document.createElement('script');
        el.type = 'application/ld+json';
        el.setAttribute('data-seo-ld', id);
        document.head.appendChild(el);
        added.push(el);
      }
      el.text = JSON.stringify(obj);
      el.setAttribute('data-seo-page', tag);
    };
    upsertLd('software', ldSoftware);
    upsertLd('faq', ldFaq);
    upsertLd('breadcrumb', ldBreadcrumb);

    return () => {
      // Stop enforcing the locale's <html lang> and restore previous value.
      langObserver.disconnect();
      document.documentElement.lang = prevHtmlLang || 'en';
      document.title = prevTitle;
      if (descPrev != null) {
        descEl.setAttribute('content', descPrev);
      }
      // Remove only the elements we created for this page
      added.forEach((el) => {
        if (el.parentNode) el.parentNode.removeChild(el);
      });
    };
  }, [locale]);
}

// ---------------------------------------------------------------------------
// Header / Footer (visually identical to LandingPage but dedicated here)
// ---------------------------------------------------------------------------

function SeoHeader({ locale, content }) {
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 10);
    window.addEventListener('scroll', onScroll);
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  return (
    <header
      className={`fixed inset-x-0 top-0 z-50 transition-all duration-300 ${
        scrolled
          ? 'bg-background/85 backdrop-blur-xl border-b border-white/5'
          : 'bg-transparent'
      }`}
      data-testid={`seo-header-${locale}`}
    >
      <div className="mx-auto max-w-7xl px-4 md:px-8 h-16 md:h-20 flex items-center justify-between">
        <Link to="/" className="font-heading text-xl md:text-2xl font-black tracking-tightish">
          Space<span className="text-primary">LeadX</span>
          <span className="text-primary/60">.AI</span>
        </Link>

        <nav className="hidden md:flex items-center gap-8">
          <Link
            to="/#features"
            className="text-sm font-semibold text-muted-foreground hover:text-foreground transition-colors"
          >
            {content.nav.features}
          </Link>
          <Link
            to="/#pricing"
            className="text-sm font-semibold text-muted-foreground hover:text-foreground transition-colors"
          >
            {content.nav.pricing}
          </Link>
          <Link
            to={locale === 'en' ? PATHS.fr : PATHS.en}
            className="text-sm font-semibold text-muted-foreground hover:text-foreground transition-colors"
            data-testid="seo-lang-switch"
          >
            {locale === 'en' ? 'FR' : 'EN'}
          </Link>
        </nav>

        <div className="flex items-center gap-2 md:gap-3">
          <Button asChild variant="ghost" size="sm" className="hidden md:inline-flex" data-testid="seo-header-signin">
            <Link to="/login">{content.headerSignIn}</Link>
          </Button>
          <Button asChild size="sm" data-testid="seo-header-cta">
            <Link to="/signup">
              {content.headerCta}
              <ArrowRight className="ml-1 h-4 w-4" />
            </Link>
          </Button>
        </div>
      </div>
    </header>
  );
}

function SeoFooter({ locale, content }) {
  const year = new Date().getFullYear();
  return (
    <footer className="border-t border-white/5 py-10 md:py-14" data-testid={`seo-footer-${locale}`}>
      <div className="mx-auto max-w-7xl px-4 md:px-8 flex flex-col md:flex-row items-start md:items-center justify-between gap-6">
        <div>
          <Link to="/" className="font-heading text-lg font-black tracking-tightish">
            Space<span className="text-primary">LeadX</span>
            <span className="text-primary/60">.AI</span>
          </Link>
          <p className="mt-2 text-xs text-muted-foreground">
            © {year} SpaceLeadX. {content.footer.tagline}
          </p>
        </div>
        <div className="flex flex-wrap gap-x-6 gap-y-2 text-sm">
          {content.footer.links.map((l) => (
            <Link
              key={l.label}
              to={l.to}
              className="text-muted-foreground hover:text-foreground transition-colors"
            >
              {l.label}
            </Link>
          ))}
        </div>
      </div>
    </footer>
  );
}

// ---------------------------------------------------------------------------
// Page body
// ---------------------------------------------------------------------------

export default function AiLeadGenPage({ locale }) {
  useSeoHead(locale);
  const c = useMemo(() => CONTENT[locale], [locale]);
  // Always scroll to top when this page mounts (SPA navigation).
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [locale]);

  return (
    <div
      className="min-h-screen bg-background text-foreground antialiased"
      data-testid={`seo-page-ai-lead-gen-${locale}`}
    >
      <SeoHeader locale={locale} content={c} />
      <main>
        {/* HERO */}
        <section className="relative pt-28 md:pt-36 pb-16 md:pb-24 overflow-hidden">
          <div
            className="absolute inset-0 -z-10 opacity-60"
            style={{
              background:
                'radial-gradient(60% 50% at 50% 0%, rgba(59, 130, 246, 0.22) 0%, rgba(59, 130, 246, 0.06) 35%, transparent 70%)',
            }}
          />
          <div
            className="absolute inset-0 -z-10 opacity-[0.04]"
            style={{
              backgroundImage:
                'linear-gradient(rgba(255,255,255,1) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,1) 1px, transparent 1px)',
              backgroundSize: '64px 64px',
              maskImage: 'radial-gradient(ellipse at top, black 0%, transparent 70%)',
            }}
          />

          <div className="relative mx-auto max-w-[1200px] px-4 md:px-8">
            {/* Breadcrumb */}
            <nav
              aria-label="Breadcrumb"
              className="text-xs md:text-sm text-muted-foreground mb-6"
              data-testid="seo-breadcrumb"
            >
              <ol className="flex items-center gap-2">
                <li>
                  <Link to="/" className="hover:text-foreground transition-colors">
                    {c.breadcrumbHome}
                  </Link>
                </li>
                <li aria-hidden="true">/</li>
                <li className="text-foreground">{c.breadcrumbCurrent}</li>
              </ol>
            </nav>

            <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full border border-white/10 bg-white/[0.03] backdrop-blur text-xs md:text-sm">
              <span className="relative flex h-2 w-2">
                <span className="absolute inline-flex h-full w-full rounded-full bg-primary opacity-75 animate-ping" />
                <span className="relative inline-flex h-2 w-2 rounded-full bg-primary" />
              </span>
              <span className="text-muted-foreground">{c.hero.eyebrow}</span>
            </div>

            <h1 className="mt-6 md:mt-8 font-heading text-4xl sm:text-5xl lg:text-6xl font-black tracking-tighter2 leading-[1.1] max-w-4xl">
              {c.hero.h1}
            </h1>

            <p className="mt-6 max-w-2xl text-base md:text-lg text-muted-foreground leading-relaxed">
              {c.hero.sub}
            </p>

            <div className="mt-8 md:mt-10 flex flex-col sm:flex-row gap-3">
              <Button
                asChild
                size="lg"
                className="h-12 md:h-14 px-7 text-base font-bold"
                data-testid="seo-hero-cta-primary"
              >
                <Link to="/#pricing">
                  <Sparkles className="mr-2 h-5 w-5" />
                  {c.hero.ctaPrimary}
                </Link>
              </Button>
              <Button
                asChild
                variant="outline"
                size="lg"
                className="h-12 md:h-14 px-7 text-base font-bold"
                data-testid="seo-hero-cta-secondary"
              >
                <Link to="/signup">
                  {c.hero.ctaSecondary}
                  <ChevronRight className="ml-1 h-5 w-5" />
                </Link>
              </Button>
            </div>

            <div className="mt-10 flex flex-wrap items-center gap-x-6 gap-y-2 text-xs text-muted-foreground">
              {c.hero.trust.map(({ icon: Icon, label }) => (
                <span key={label} className="flex items-center gap-1.5">
                  <Icon className="h-3.5 w-3.5 text-emerald-400" />
                  {label}
                </span>
              ))}
            </div>
          </div>
        </section>

        {/* SECTION 1 */}
        <section className="py-16 md:py-24" data-testid="seo-section-1">
          <div className="mx-auto max-w-[1200px] px-4 md:px-8 grid md:grid-cols-5 gap-10">
            <div className="md:col-span-2">
              <Badge variant="outline" className="mb-4 border-primary/40 text-primary bg-primary/10">
                01
              </Badge>
              <h2 className="font-heading text-3xl md:text-4xl font-black tracking-tighter2 leading-tight">
                {c.section1.h2}
              </h2>
            </div>
            <div className="md:col-span-3 space-y-4 text-base md:text-lg text-muted-foreground leading-relaxed">
              {c.section1.paragraphs.map((p, i) => (
                <p key={i}>{p}</p>
              ))}
            </div>
          </div>
        </section>

        {/* SECTION 2 — features grid */}
        <section className="py-16 md:py-24 border-t border-white/5" data-testid="seo-section-2">
          <div className="mx-auto max-w-[1200px] px-4 md:px-8">
            <h2 className="font-heading text-3xl md:text-4xl font-black tracking-tighter2 leading-tight max-w-3xl">
              {c.section2.h2}
            </h2>
            <div className="mt-10 md:mt-14 grid grid-cols-2 md:grid-cols-4 gap-3 md:gap-4">
              {c.section2.items.map(({ icon: Icon, label }) => (
                <div
                  key={label}
                  className="flex flex-col items-start gap-3 p-5 rounded-xl border border-white/10 bg-white/[0.02] hover:bg-white/[0.04] transition-colors"
                  data-testid={`seo-feature-item-${label}`}
                >
                  <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-primary/10 text-primary">
                    <Icon className="h-5 w-5" />
                  </div>
                  <p className="text-sm md:text-base font-semibold leading-snug">{label}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* SECTION 3 — benefit cards */}
        <section className="py-16 md:py-24 border-t border-white/5" data-testid="seo-section-3">
          <div className="mx-auto max-w-[1200px] px-4 md:px-8">
            <h2 className="font-heading text-3xl md:text-4xl font-black tracking-tighter2 leading-tight max-w-3xl">
              {c.section3.h2}
            </h2>
            <div className="mt-10 md:mt-14 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-6">
              {c.section3.cards.map(({ icon: Icon, title, desc }) => (
                <Card key={title} className="group relative overflow-hidden" data-testid={`seo-benefit-${title}`}>
                  <CardContent className="relative p-6 md:p-7">
                    <div className="flex h-11 w-11 items-center justify-center rounded-xl bg-primary/10 text-primary group-hover:bg-primary/20 transition-colors">
                      <Icon className="h-5 w-5" />
                    </div>
                    <h3 className="mt-4 font-heading text-lg md:text-xl font-bold tracking-tightish">
                      {title}
                    </h3>
                    <p className="mt-2 text-sm md:text-base text-muted-foreground leading-relaxed">
                      {desc}
                    </p>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        </section>

        {/* SECTION 4 — industries */}
        <section className="py-16 md:py-24 border-t border-white/5" data-testid="seo-section-4">
          <div className="mx-auto max-w-[1200px] px-4 md:px-8">
            <div className="max-w-3xl">
              <h2 className="font-heading text-3xl md:text-4xl font-black tracking-tighter2 leading-tight">
                {c.section4.h2}
              </h2>
              <p className="mt-4 text-base md:text-lg text-muted-foreground leading-relaxed">
                {c.section4.paragraph}
              </p>
            </div>
            <div className="mt-10 md:mt-14 grid grid-cols-2 md:grid-cols-3 gap-3 md:gap-4">
              {c.section4.industries.map(({ icon: Icon, label }) => (
                <div
                  key={label}
                  className="flex items-center gap-3 p-4 rounded-xl border border-white/10 bg-white/[0.02] hover:bg-white/[0.04] transition-colors"
                  data-testid={`seo-industry-${label}`}
                >
                  <div className="flex h-9 w-9 items-center justify-center rounded-lg bg-primary/10 text-primary shrink-0">
                    <Icon className="h-4.5 w-4.5" />
                  </div>
                  <p className="text-sm md:text-base font-semibold">{label}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* SECTION 5 */}
        <section className="py-16 md:py-24 border-t border-white/5" data-testid="seo-section-5">
          <div className="mx-auto max-w-[1200px] px-4 md:px-8 grid md:grid-cols-5 gap-10">
            <div className="md:col-span-2">
              <Badge variant="outline" className="mb-4 border-primary/40 text-primary bg-primary/10">
                05
              </Badge>
              <h2 className="font-heading text-3xl md:text-4xl font-black tracking-tighter2 leading-tight">
                {c.section5.h2}
              </h2>
            </div>
            <div className="md:col-span-3 space-y-4 text-base md:text-lg text-muted-foreground leading-relaxed">
              {c.section5.paragraphs.map((p, i) => (
                <p key={i}>{p}</p>
              ))}
            </div>
          </div>
        </section>

        {/* SECTION 6 */}
        <section className="py-16 md:py-24 border-t border-white/5" data-testid="seo-section-6">
          <div className="mx-auto max-w-[1200px] px-4 md:px-8 grid md:grid-cols-5 gap-10">
            <div className="md:col-span-2">
              <Badge variant="outline" className="mb-4 border-primary/40 text-primary bg-primary/10">
                06
              </Badge>
              <h2 className="font-heading text-3xl md:text-4xl font-black tracking-tighter2 leading-tight">
                {c.section6.h2}
              </h2>
            </div>
            <div className="md:col-span-3 space-y-4 text-base md:text-lg text-muted-foreground leading-relaxed">
              {c.section6.paragraphs.map((p, i) => (
                <p key={i}>{p}</p>
              ))}
            </div>
          </div>
        </section>

        {/* SECTION 7 */}
        <section className="py-16 md:py-24 border-t border-white/5" data-testid="seo-section-7">
          <div className="mx-auto max-w-[1200px] px-4 md:px-8 grid md:grid-cols-5 gap-10">
            <div className="md:col-span-2">
              <Badge variant="outline" className="mb-4 border-primary/40 text-primary bg-primary/10">
                07
              </Badge>
              <h2 className="font-heading text-3xl md:text-4xl font-black tracking-tighter2 leading-tight">
                {c.section7.h2}
              </h2>
            </div>
            <div className="md:col-span-3 space-y-4 text-base md:text-lg text-muted-foreground leading-relaxed">
              {c.section7.paragraphs.map((p, i) => (
                <p key={i}>{p}</p>
              ))}
            </div>
          </div>
        </section>

        {/* FAQ */}
        <section className="py-16 md:py-24 border-t border-white/5" data-testid="seo-faq">
          <div className="mx-auto max-w-3xl px-4 md:px-8">
            <h2 className="font-heading text-3xl md:text-4xl font-black tracking-tighter2 leading-tight">
              {c.faq.h2}
            </h2>
            <Accordion type="single" collapsible className="mt-8 md:mt-10">
              {c.faq.items.map((it, i) => (
                <AccordionItem key={i} value={`faq-${i}`} className="border-white/10">
                  <AccordionTrigger
                    className="text-left text-base md:text-lg font-bold hover:no-underline"
                    data-testid={`seo-faq-trigger-${i}`}
                  >
                    {it.q}
                  </AccordionTrigger>
                  <AccordionContent className="text-sm md:text-base text-muted-foreground leading-relaxed">
                    {it.a}
                  </AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </div>
        </section>

        {/* Internal links band — only to routes/anchors that actually exist */}
        <section className="py-12 md:py-16 border-t border-white/5" data-testid="seo-internal-links">
          <div className="mx-auto max-w-[1200px] px-4 md:px-8">
            <h3 className="font-heading text-xl md:text-2xl font-bold tracking-tightish">
              {c.internalLinks.h3}
            </h3>
            <div className="mt-5 flex flex-wrap gap-3">
              {c.internalLinks.items.map((l) => (
                <Link
                  key={l.label}
                  to={l.to}
                  className="inline-flex items-center gap-1.5 rounded-full border border-white/10 bg-white/[0.03] px-4 py-2 text-sm font-semibold text-muted-foreground hover:text-foreground hover:bg-white/[0.06] transition-colors"
                  data-testid={`seo-internal-link-${l.label}`}
                >
                  {l.label}
                  <ChevronRight className="h-4 w-4" />
                </Link>
              ))}
            </div>
          </div>
        </section>

        {/* FINAL CTA */}
        <section className="py-20 md:py-28 border-t border-white/5" data-testid="seo-final-cta">
          <div className="mx-auto max-w-4xl px-4 md:px-8">
            <Card className="relative overflow-hidden border-primary/30 bg-gradient-to-br from-primary/15 via-primary/5 to-transparent">
              <div
                className="absolute inset-0 opacity-50"
                style={{
                  background:
                    'radial-gradient(60% 80% at 50% 0%, rgba(59,130,246,0.25) 0%, transparent 70%)',
                }}
              />
              <CardContent className="relative p-8 md:p-14 text-center space-y-6">
                <h2 className="font-heading text-3xl md:text-5xl font-black tracking-tighter2 leading-tight max-w-2xl mx-auto">
                  {c.finalCta.h2}
                </h2>
                <p className="text-base md:text-lg text-muted-foreground max-w-xl mx-auto">
                  {c.finalCta.text}
                </p>
                <Button
                  asChild
                  size="lg"
                  className="h-12 md:h-14 px-8 text-base font-bold"
                  data-testid="seo-final-cta-btn"
                >
                  <Link to="/#pricing">
                    <Sparkles className="mr-2 h-5 w-5" />
                    {c.finalCta.button}
                  </Link>
                </Button>
              </CardContent>
            </Card>
          </div>
        </section>
      </main>
      <SeoFooter locale={locale} content={c} />
    </div>
  );
}
